// src/services/socket/UniversalResponseHandler.js - Pure Event-Based Pattern

/**
 * Universal Response Handler - Simplified Event-Based Version
 * 
 * This is MUCH simpler than the request-response version!
 * 
 * What it does:
 * - Backend sends: emit('response', {event: 'events_loaded', data: {...}})
 * - We convert that to: emit('events_loaded', data) for the frontend
 * 
 * That's it! No request correlation, no timeouts, no complexity.
 * 
 * Usage:
 *   handler.on('events_loaded', (data) => console.log(data));
 *   socket.emit('get_events', {start_date: '2025-07-13'});
 */
class UniversalResponseHandler {
  constructor(socket) {
    this.socket = socket;
    this.eventCallbacks = new Map(); // event name -> array of callback functions
    this.setupUniversalListener();
    console.log('🎯 UniversalResponseHandler initialized (event-based mode)');
  }

  /**
   * The magic happens here - we listen for ALL backend responses
   * and convert them to individual events that the frontend can listen to
   */
  setupUniversalListener() {
    this.socket.on('response', (responseData) => {
      // Backend sends: {status: 'success', event: 'events_loaded', data: {...}, timestamp: '...'}
      const eventName = responseData.event;

      if (!eventName) {
        console.warn('⚠️ Received response without event name:', responseData);
        return;
      }

      console.log(`📡 Received event: ${eventName}`, responseData.status);

      // FIRST: Emit to generic 'response' listeners (for global handlers like NotificationListener)
      // This allows listeners to intercept ALL responses before specific event handling
      if (this.eventCallbacks.has('response')) {
        const responseCallbacks = this.eventCallbacks.get('response');
        responseCallbacks.forEach(callback => {
          try {
            // Always pass full response data to generic listeners
            callback(responseData);
          } catch (error) {
            console.error(`💥 Error in generic response callback:`, error);
          }
        });
      }

      // THEN: Handle specific event callbacks (existing behavior for stores)
      if (this.eventCallbacks.has(eventName)) {
        const callbacks = this.eventCallbacks.get(eventName);

        // Call each callback with the appropriate payload
        callbacks.forEach(callback => {
          try {
            if (responseData.status === 'success') {
              // For success: pass just the data (clean and simple)
              callback(responseData.data);
            } else {
              // For errors: pass the full response so they can see the error message
              callback(responseData);
            }
          } catch (error) {
            console.error(`💥 Error in ${eventName} callback:`, error);
          }
        });
      } else {
        console.log(`📭 No listeners for event: ${eventName}`);
      }
    });
  }

  /**
   * Register a callback for a specific event
   * This is like socket.on() but works with our backend's universal response format
   * 
   * @param {string} eventName - Event to listen for (e.g., 'events_loaded')
   * @param {function} callback - Function to call when event happens
   */
  on(eventName, callback) {
    if (!this.eventCallbacks.has(eventName)) {
      this.eventCallbacks.set(eventName, []);
    }
    
    this.eventCallbacks.get(eventName).push(callback);
    console.log(`👂 Listening for: ${eventName} (${this.eventCallbacks.get(eventName).length} listeners)`);
  }

  /**
   * Remove a specific callback for an event
   * 
   * @param {string} eventName - Event name
   * @param {function} callback - Specific callback to remove
   */
  off(eventName, callback) {
    if (this.eventCallbacks.has(eventName)) {
      const callbacks = this.eventCallbacks.get(eventName);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        console.log(`🔇 Removed listener for: ${eventName} (${callbacks.length} remaining)`);
        
        // Clean up empty arrays to prevent memory leaks
        if (callbacks.length === 0) {
          this.eventCallbacks.delete(eventName);
        }
      }
    }
  }

  /**
   * Emit an event to the server
   * This is just a convenience method - you could call socket.emit() directly
   * 
   * @param {string} eventName - Event to send
   * @param {object} data - Data to send
   */
  emit(eventName, data = {}) {
    console.log(`📤 Emitting: ${eventName}`);
    this.socket.emit(eventName, data);
  }

  /**
   * Clean up all listeners when shutting down
   */
  cleanup() {
    this.eventCallbacks.clear();
    console.log('🧹 Universal response handler cleaned up');
  }
}

export default UniversalResponseHandler;